import { useContext, useEffect, useState, useRef, useCallback } from "react";
import SocketClient from "../../socket-client";
import { WidgetContext } from "../../store/widgetContext";
import { SessionManager } from "../../session/SessionManager";
import { isCurrentUrlAllowed } from "../helpers/domainValidation";
import { fetchWidget } from "./fetchWidget";
import { showMessenger, hideMessengerFrame, setVerifiedParentOrigin, postToParent } from "../host-bridge";

/**
 * Handles bootstrap: postMessage (verifyOrigin, routeChange, intelswift:session),
 * fetchWidget, and SocketClient init. PrivateRoutes uses this and only renders Routes when ready.
 */
export function useBootstrap(props) {
	const { assistantId, botId, lang, tenantId, uuid, sandbox } = props;
	const context = useContext(WidgetContext);
	const { setWidget, setAssistant, setPathName, setHostName } = context;

	const socketRef = useRef(null);
	const pendingVerifyRef = useRef(null);
	const domainsRef = useRef(false);

	const [initializedSocket, setInitializedSocket] = useState(false);
	const [loading, setLoading] = useState(true);
	const [pendingVerifyVersion, setPendingVerifyVersion] = useState(0);
	const [verified, setVerified] = useState(false);

	const verifyOrigin = useCallback(
		(event) => {
			try {
				const origin = event.origin;
				const hrefPath = event.data.href ? new URL(event.data.href).pathname : "";
				const safeUrl = new URL(origin);
				safeUrl.pathname = hrefPath;

				if (sandbox && (origin.includes("localhost") || origin.includes("intelswift"))) {
					setVerifiedParentOrigin(origin);
					setVerified(true);
					showMessenger();
				} else if (Array.isArray(domainsRef.current) && domainsRef.current.length > 0) {
					const allowed = isCurrentUrlAllowed(safeUrl, domainsRef.current);
					setVerified(allowed);
					if (allowed) {
						setVerifiedParentOrigin(origin);
						showMessenger();
					} else {
						console.warn("[IFRAME] Origin not allowed:", new URL(origin).hostname);
						hideMessengerFrame();
					}
				}
			} catch (err) {
				console.error("[IFRAME] Origin validation error:", err);
			}
		},
		[sandbox]
	);

	const runFetchWidget = useCallback(() => {
		if (!tenantId || !botId || !lang || !uuid || pendingVerifyRef?.current?.data?.type !== "verifyOrigin") return;
		fetchWidget(botId, tenantId, lang, assistantId, uuid, pendingVerifyRef.current, {
			setPathName,
			setHostName,
			setAssistant,
			setWidget,
			setLoading,
			onDomainsLoaded: (domains) => {
				domainsRef.current = domains;
				if (pendingVerifyRef.current) {
					verifyOrigin(pendingVerifyRef.current);
				}
			}
		});
	}, [tenantId, botId, lang, assistantId, uuid, setPathName, setHostName, setAssistant, setWidget, verifyOrigin]);

	useEffect(() => {
		const handleMessage = (event) => {
			if (event.data?.type === "verifyOrigin" && event.data.href) {
				if (!domainsRef.current || !Array.isArray(domainsRef.current)) {
					console.warn("[VERIFY] Domains not ready, deferring verification...");
					pendingVerifyRef.current = event;
					setPendingVerifyVersion((v) => v + 1);
					return;
				}
				verifyOrigin(event);
			}
			if (event.data?.type === "routeChange" && event.data.url) {
				const url = new URL(event.data.url);
				const allowed = isCurrentUrlAllowed(url, domainsRef.current);
				setVerified(allowed);
				if (allowed) {
					setVerifiedParentOrigin(url.origin);
					showMessenger();
				} else {
					hideMessengerFrame();
				}
			}
			if (event.data?.type === "intelswift:session") {
				SessionManager.handleSessionEvent(event.data.detail);
			}
		};

		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, [verifyOrigin]);

	// `assistantId` is optional (D-R21): an "Operators only" channel has none, and the bootstrap names the agent.
	useEffect(() => {
		if (tenantId && botId && uuid) {
			postToParent({ type: "messengerReady" });
		}
	}, [tenantId, botId, uuid]);

	useEffect(() => {
		runFetchWidget();
	}, [runFetchWidget, pendingVerifyVersion]);

	useEffect(() => {
		if (!uuid || !tenantId || !botId) return;

		const initSocket = () => {
			const rawContactId = SessionManager.getRawContactId();
			socketRef.current = SocketClient.getInstance(tenantId, rawContactId, botId);
		};

		if (!initializedSocket) {
			initSocket();
			setInitializedSocket(true);
		}

		const unsubscribe = SessionManager.onIdentityChange(() => {
			SocketClient.destroyInstance();
			const rawContactId = SessionManager.getRawContactId();
			socketRef.current = SocketClient.getInstance(tenantId, rawContactId, botId);
		});

		return () => unsubscribe();
	}, [uuid, tenantId, botId, initializedSocket]);

	return { loading, verified };
}
